import { useEffect, useState } from 'react';
import { learningApi } from '../../../api/learning.js';
import { hrApi } from '../../../api/hr.js';

/** Compliance training — bulk-assign a course + per-employee assigned/completed/overdue dashboard. */
export default function Compliance() {
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dashboard, setDashboard] = useState({ items: [], byEmployee: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  function refresh() {
    setLoading(true);
    Promise.all([
      learningApi.listCourses(),
      hrApi.listEmployees().catch(() => ({ items: [] })),
      learningApi.complianceDashboard(),
    ])
      .then(([c, e, d]) => {
        setCourses(c?.items || []);
        setEmployees(e?.items || e || []);
        setDashboard(d || { items: [], byEmployee: [], summary: {} });
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load compliance'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function assign(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body = {
      courseId: fd.get('courseId'),
      dueAt: fd.get('dueAt') || null,
      recurrence: fd.get('recurrence') || 'once',
    };
    const emp = fd.get('employeeId');
    const role = fd.get('role');
    if (emp) body.employeeIds = [emp];
    if (role) body.role = role;
    try {
      const res = await learningApi.assignCompliance(body);
      setNotice(`Assigned to ${res.assigned} employee(s)${res.skipped ? `, ${res.skipped} already assigned` : ''}.`);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign');
    }
  }

  const s = dashboard.summary || {};

  return (
    <section data-testid="learning-compliance-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Compliance Training</h1>
          <p className="muted">{s.total || 0} assignments · {s.overdue || 0} overdue</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="learning-compliance-error">{error}</div>}
      {notice && <div className="alert info" data-testid="learning-compliance-notice">{notice}</div>}

      <form onSubmit={assign} className="card form-card grid-form">
        <label>Course
          <select name="courseId" data-testid="learning-compliance-course-input" required defaultValue="">
            <option value="" disabled>— select course —</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </label>
        <label>Employee
          <select name="employeeId" data-testid="learning-compliance-employee-input" defaultValue="">
            <option value="">— none —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        <label>Or role<input name="role" placeholder="e.g. developer" data-testid="learning-compliance-role-input" /></label>
        <label>Due date<input name="dueAt" type="date" data-testid="learning-compliance-due-input" /></label>
        <label>Recurrence
          <select name="recurrence" defaultValue="once" data-testid="learning-compliance-recurrence-input">
            <option value="once">Once</option>
            <option value="annual">Annual</option>
            <option value="biennial">Biennial</option>
          </select>
        </label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="learning-compliance-assign">Assign</button></div>
      </form>

      <div className="card">
        <h3>Tracking dashboard</h3>
        <table className="data-table" data-testid="learning-compliance-list">
          <thead><tr><th>Employee</th><th>Assigned</th><th>Completed</th><th>Overdue</th></tr></thead>
          <tbody>
            {(dashboard.byEmployee || []).map((row) => (
              <tr key={row.employee_id} data-testid={`learning-compliance-row-${row.employee_id}`} className={row.overdue > 0 ? 'row-warn' : ''}>
                <td>{row.employee_name || row.employee_code || row.employee_id}</td>
                <td>{row.assigned}</td>
                <td>{row.completed}</td>
                <td data-testid={`learning-compliance-overdue-${row.employee_id}`}>{row.overdue}</td>
              </tr>
            ))}
            {!loading && (dashboard.byEmployee || []).length === 0 && <tr><td colSpan={4} className="muted">No compliance assignments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
